import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Database, RefreshCw, Eye, EyeOff } from 'lucide-react';

export default function AdminCollections() {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionData, setCollectionData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = () => {
    const availableCollections = [
      { name: 'users', label: 'Kullanıcılar', description: 'Tüm sistem kullanıcıları' },
      { name: 'exhibitions', label: 'Fuarlar', description: 'Fuar bilgileri' },
      { name: 'layouts', label: 'Krokiler', description: 'Fuar kroki verileri' },
      { name: 'stand_requests', label: 'Stant Talepleri', description: 'Firma talepleri' },
      { name: 'assignments', label: 'Atamalar', description: 'Stant atamaları' },
      { name: 'password_reset_codes', label: 'Şifre Kodları', description: 'Sıfırlama kodları' },
    ];
    setCollections(availableCollections);
  };

  const loadCollectionData = async (collectionName) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      switch(collectionName) {
        case 'users':
          endpoint = `${API}/users`;
          break;
        case 'exhibitions':
          endpoint = `${API}/exhibitions`;
          break;
        case 'stand_requests':
          endpoint = `${API}/requests`;
          break;
        case 'assignments':
          endpoint = `${API}/assignments`;
          break;
        default:
          toast.error('Bu collection için görüntüleyici henüz eklenmedi');
          setLoading(false);
          return;
      }
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCollectionData(res.data);
      setSelectedCollection(collectionName);
      setLoading(false);
    } catch (error) {
      toast.error('Veri yüklenemedi');
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="admin-collections">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">MongoDB Collections</h1>
        <p className="text-gray-500 mt-1">Veritabanı koleksiyonlarını görüntüle ve yönet</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Collections List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Collections
            </CardTitle>
            <CardDescription>{collections.length} koleksiyon mevcut</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {collections.map((collection) => (
                  <button
                    key={collection.name}
                    onClick={() => loadCollectionData(collection.name)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedCollection === collection.name
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid={`collection-${collection.name}`}
                  >
                    <div className="font-medium text-sm">{collection.label}</div>
                    <div className="text-xs text-gray-500 mt-1">{collection.description}</div>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Collection Data */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {selectedCollection ? 
                    collections.find(c => c.name === selectedCollection)?.label : 
                    'Collection Data'
                  }
                </CardTitle>
                <CardDescription>
                  {selectedCollection ? `${collectionData.length} kayıt` : 'Bir collection seçin'}
                </CardDescription>
              </div>
              {selectedCollection && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => loadCollectionData(selectedCollection)}
                  disabled={loading}
                  data-testid="refresh-collection"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedCollection ? (
              <div className="text-center py-12 text-gray-500">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Sol menüden bir collection seçin</p>
              </div>
            ) : loading ? (
              <div className="text-center py-12 text-gray-500">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
                <p>Yükleniyor...</p>
              </div>
            ) : collectionData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>Bu collection'da henüz veri yok</p>
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-4">
                  {collectionData.map((item, index) => (
                    <Card key={item.id || index} className="p-4" data-testid={`collection-item-${index}`}>
                      <pre className="text-xs overflow-x-auto">
                        {JSON.stringify(item, null, 2)}
                      </pre>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}